import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import * as api from 'api/api'
import { ApprovalListItem, Section } from 'api/types'
import { useAuth } from 'auth/AuthContext'
import { useRegion } from 'components/app-shell/context/RegionContext'
import { ApprovalCard } from 'components/ApprovalCard'
import { ComplianceCard, ComplianceRow, ComplianceStatus } from 'components/ComplianceCard'
import { useConfirm } from 'components/Confirm'
import { Icon } from 'components/Icon'
import { ReportStatusChart, SiteStatusCounts } from 'components/ReportStatusChart'
import { ReturnReportDialog } from 'components/ReturnReportDialog'
import { Card, EmptyState, ErrorState, Skeleton, Spinner } from 'components/ui'
import { STATUS_LABELS, STATUS_LABELS_EN, WEEK_DAYS, WEEK_DAYS_EN, addDays, formatDate, roleLabel, weekStart } from 'lib/format'
import { tx } from 'lib/i18n'
import { useAction, useAsync } from 'lib/hooks'

const DashboardSkeleton = () => (
  <div className="page" aria-busy="true">
    <div className="dashboard-skeleton__greeting">
      <Skeleton width={160} height={16} />
      <Skeleton width={280} height={16} />
    </div>
    <div className="dashboard-skeleton__stats">
      {[0, 1, 2].map((index) => (
        <div className="dashboard-skeleton__stat" key={index}>
          <Skeleton width={96} height={12} />
          <Skeleton width={64} height={28} />
        </div>
      ))}
    </div>
    {[0, 1, 2].map((index) => (
      <div className="dashboard-skeleton__card" key={index}>
        <div className="dashboard-skeleton__card-head">
          <Skeleton width={32} height={32} circle />
          <div className="dashboard-skeleton__card-lines">
            <Skeleton width={160} height={16} />
            <Skeleton width={96} height={16} />
          </div>
        </div>
        <Skeleton height={96} />
        <Skeleton height={16} />
        <Skeleton width={180} height={16} />
      </div>
    ))}
  </div>
)

export const DashboardPage = () => {
  const { user } = useAuth()
  const { locale } = useRegion()
  const { busy, run } = useAction()
  const confirm = useConfirm()
  const { data: today, loading: todayLoading } = useAsync(api.fetchToday, [])
  const from = today ? weekStart(today) : undefined
  const to = from ? addDays(from, 6) : undefined

  const {
    data: weekReports,
    error: weekError,
    loading: weekLoading,
    reload: reloadWeek,
  } = useAsync(() => (from && to ? api.fetchMyReports(from, to) : Promise.resolve([])), [from, to])

  const {
    data: pending,
    error: pendingError,
    loading: pendingLoading,
    reload: reloadPending,
  } = useAsync(() => api.fetchApprovals('PENDING'), [])

  const { data: projects, loading: projectsLoading } = useAsync(api.fetchProjects, [])

  const from30d = today ? addDays(today, -29) : undefined

  const { data: monthReports, loading: monthLoading } = useAsync(
    () => (from30d && today ? api.fetchMyReports(from30d, today) : Promise.resolve([])),
    [from30d, today]
  )

  const initialLoading =
    (todayLoading && !today) || (weekLoading && !weekReports) || (pendingLoading && !pending) || (projectsLoading && !projects)

  const sites = (projects ?? []).filter((project) => project.myRole === 'ADMIN' || project.myRole === 'PROJECT_MANAGER')
  const pendingItems = pending ?? []
  const weekItems = weekReports ?? []
  const missingThisWeek = weekItems.filter((item) => item.status === 'MISSING').length

  const byProjectDay = useMemo(() => {
    const map = new Map<string, Map<string, (typeof weekItems)[number]>>()
    ;(weekReports ?? []).forEach((item) => {
      if (!map.has(item.projectId)) map.set(item.projectId, new Map())
      map.get(item.projectId)?.set(item.reportDate, item)
    })
    return map
  }, [weekReports])

  const days = from ? Array.from({ length: 7 }, (_, index) => addDays(from, index)) : []

  const cellStatus = (item: ApprovalListItem | undefined, day: string): ComplianceStatus => {
    if (!item) return 'none'
    if (item.status === 'COMPLETE' || item.status === 'CREATED') {
      if (!item.submittedAt) return 'onTime'
      const hour = Number(
        new Intl.DateTimeFormat('en-US', { hour: 'numeric', hour12: false, timeZone: 'Asia/Amman' }).format(new Date(item.submittedAt))
      )
      return hour < 18 ? 'onTime' : 'late'
    }
    if (item.status === 'MISSING') return 'missed'
    if (item.status === 'DRAFT') return today && day < today ? 'missed' : 'upcoming'
    return 'upcoming'
  }

  const complianceRows: ComplianceRow[] = useMemo(
    () =>
      sites.map((site) => ({
        cells: days.map((day) => {
          const item = byProjectDay.get(site.id)?.get(day)
          return {
            href: item ? `/projects/${site.id}/reports/${item.id}` : undefined,
            status: cellStatus(item, day),
            title: item ? `${locale === 'en' ? STATUS_LABELS_EN[item.status] : STATUS_LABELS[item.status]} · ${item.code}` : undefined,
          }
        }),
        siteId: site.id,
        siteName: site.name,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sites, days.join('|'), byProjectDay, today, locale]
  )

  const statusChartData: SiteStatusCounts[] = useMemo(
    () =>
      sites.map((site) => {
        const items = (monthReports ?? []).filter((item) => item.projectId === site.id)
        return {
          approved: items.filter((item) => item.status === 'COMPLETE').length,
          draft: items.filter((item) => item.status === 'DRAFT').length,
          overdue: items.filter((item) => item.status === 'MISSING').length,
          site: site.name,
          submitted: items.filter((item) => item.status === 'CREATED').length,
        }
      }),
    [sites, monthReports]
  )

  const [returning, setReturning] = useState<ApprovalListItem | null>(null)

  const approveOne = async (item: ApprovalListItem) => {
    if (!(await confirm(tx(locale, 'اعتماد هذا التقرير؟', 'Approve this report?'), { confirmLabel: tx(locale, 'اعتماد', 'Approve'), tone: 'primary' }))) return
    const result = await run(`approve-${item.id}`, () => api.approveReports([item.id]), tx(locale, 'تم اعتماد التقرير', 'Report approved'))
    if (result) {
      reloadWeek()
      reloadPending()
    }
  }

  const confirmReturn = async (note: string, sections: Section[]) => {
    if (!returning) return
    const result = await run(`return-${returning.id}`, () => api.notedTransition('returnReport', returning.id, note))
    if (result) {
      await Promise.all(sections.map((section) => api.addSupervisionNote(returning.id, note, section)))
      setReturning(null)
      reloadWeek()
      reloadPending()
    }
  }

  const firstName = user?.name?.trim().split(' ')[0] ?? ''

  if (initialLoading) return <DashboardSkeleton />

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1>{tx(locale, `مرحباً، ${firstName}`, `Hello, ${firstName}`)}</h1>
          <p className="muted">
            {tx(
              locale,
              `${sites.length} مواقع${pendingItems.length ? ` · ${pendingItems.length} تقارير بانتظارك` : ''}`,
              `${sites.length} ${sites.length === 1 ? 'site' : 'sites'}${pendingItems.length ? ` · ${pendingItems.length} ${pendingItems.length === 1 ? 'report' : 'reports'} waiting for you` : ''}`
            )}
          </p>
        </div>
      </div>

      <div className="stats stats--static" role="group" aria-label={tx(locale, 'إحصائيات الأسبوع', 'This week')}>
        <div className="stat">
          <span className="stat__value">{weekLoading && !weekReports ? '—' : weekItems.length}</span>
          <span className="stat__label">{tx(locale, 'تقارير هذا الأسبوع', 'Reports this week')}</span>
        </div>
        <Link to="/approvals" className="stat">
          <span className="stat__value">{pendingLoading && !pending ? '—' : pendingItems.length}</span>
          <span className="stat__label">{tx(locale, 'اعتمادات بانتظارك', 'Approvals waiting for you')}</span>
        </Link>
        <div className="stat stat--missing">
          <span className="stat__value">{weekLoading && !weekReports ? '—' : missingThisWeek}</span>
          <span className="stat__label">{tx(locale, 'تقارير مفقودة هذا الأسبوع', 'Missing reports this week')}</span>
        </div>
      </div>

      <Card
        title={tx(locale, 'بانتظار اعتمادك', 'Waiting for your approval')}
        actions={
          pendingItems.length > 3 ? (
            <Link to="/approvals" className="link-button">
              {tx(locale, `عرض الكل (${pendingItems.length})`, `View all (${pendingItems.length})`)}
            </Link>
          ) : null
        }
      >
        {pendingLoading && !pending ? (
          <Spinner />
        ) : pendingError ? (
          <ErrorState message={pendingError} onRetry={reloadPending} />
        ) : !pendingItems.length ? (
          <EmptyState icon="check" title={tx(locale, 'لا توجد تقارير بانتظار اعتمادك', 'No reports are waiting for your approval')} />
        ) : (
          <div className="approval-card-grid">
            {pendingItems.slice(0, 3).map((item) => (
              <ApprovalCard
                key={item.id}
                item={item}
                busy={busy === `approve-${item.id}` || busy === `return-${item.id}`}
                onReturn={setReturning}
                onApprove={approveOne}
              />
            ))}
          </div>
        )}
      </Card>

      {returning ? (
        <ReturnReportDialog
          reportCode={returning.code}
          engineerName={returning.preparedByName}
          busy={busy === `return-${returning.id}`}
          onConfirm={confirmReturn}
          onClose={() => setReturning(null)}
        />
      ) : null}

      <Card title={tx(locale, 'إرسال التقارير هذا الأسبوع', 'Report submission this week')}>
        {weekLoading && !weekReports ? (
          <Spinner />
        ) : weekError ? (
          <ErrorState message={weekError} onRetry={reloadWeek} />
        ) : !sites.length ? (
          <EmptyState title={tx(locale, 'لا تدير أي موقع', 'You do not manage a site')} icon="mapPin" />
        ) : (
          <ComplianceCard dayLabels={locale === 'en' ? WEEK_DAYS_EN : WEEK_DAYS} rows={complianceRows} />
        )}
      </Card>

      <Card title={tx(locale, 'حالة التقارير حسب الموقع', 'Report status by site')} actions={<span className="muted small">{tx(locale, 'آخر 30 يوماً', 'Last 30 days')}</span>}>
        {monthLoading && !monthReports ? (
          <Spinner />
        ) : !sites.length ? (
          <EmptyState title={tx(locale, 'لا تدير أي موقع', 'You do not manage a site')} icon="mapPin" />
        ) : (
          <ReportStatusChart data={statusChartData} />
        )}
      </Card>

      <Card title={tx(locale, 'مواقعك', 'Your sites')}>
        {!sites.length ? (
          <EmptyState title={tx(locale, 'لا تدير أي موقع', 'You do not manage a site')} icon="mapPin" />
        ) : (
          <ul className="project-grid">
            {sites.map((site) => (
              <li key={site.id}>
                <Link to={`/projects/${site.id}`} className="project-card">
                  <div className="project-card__top">
                    <span className="chip">{roleLabel(site.myRole, locale)}</span>
                    <Icon name={locale === 'en' ? 'chevronRight' : 'chevronLeft'} />
                  </div>
                  <h2>{site.name}</h2>
                  <dl>
                    <div>
                      <dt>{tx(locale, 'المدة', 'Duration')}</dt>
                      <dd>
                        {formatDate(site.startDate)} – {formatDate(site.endDate)}
                      </dd>
                    </div>
                  </dl>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  )
}
